import { dict as en_us_dict, rtl as en_us_rlt } from './dicts/en_US'

import { ILocaleDict, LocaleID } from './types'

export default async function loadDict(
  localeID: LocaleID
): Promise<{ dict: ILocaleDict; rtl: boolean; textDir?: boolean }> {
  switch (localeID?.toLowerCase()) {
    case 'en':
    case 'en_us':
    case 'en_gb':
    case 'en_ie': {
      // PRELOAD DEFAULT LOCALE
      return { dict: en_us_dict, rtl: en_us_rlt }
    }
    case 'ar':
    case 'ar_ae': {
      const localeDict = await import('./dicts/ar')
      return { dict: localeDict.dict, rtl: localeDict.rtl, textDir: localeDict.textDir }
    }
    case 'bg':
    case 'bg_bg': {
      const localeDict = await import('./dicts/bg')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'da':
    case 'da_da':
    case 'da_dk': {
      const localeDict = await import('./dicts/da')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'de':
    case 'de_de': {
      const localeDict = await import('./dicts/de')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'fi':
    case 'fi_fi': {
      const localeDict = await import('./dicts/fi')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'fr':
    case 'fr_ca':
    case 'fr_fr': {
      const localeDict = await import('./dicts/fr')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'el':
    case 'el_gr': {
      const localeDict = await import('./dicts/el')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'es':
    case 'es_es':
    case 'es_mx':
    case 'es_us': {
      const localeDict = await import('./dicts/es')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'he':
    case 'he_il': {
      const localeDict = await import('./dicts/he')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'hi':
    case 'hi_in': {
      const localeDict = await import('./dicts/hi')
      return { dict: localeDict.dict, rtl: localeDict.rtl, textDir: localeDict.textDir }
    }
    case 'id':
    case 'id_id': {
      const localeDict = await import('./dicts/id')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'it':
    case 'it_it': {
      const localeDict = await import('./dicts/it')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'ja':
    case 'ja_jp': {
      const localeDict = await import('./dicts/ja')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'ko':
    case 'ko_kr': {
      const localeDict = await import('./dicts/ko')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'ms':
    case 'ms_ma':
    case 'ms_my': {
      const localeDict = await import('./dicts/ms')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'nl':
    case 'nl_nl': {
      const localeDict = await import('./dicts/nl')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'no':
    case 'no_no': {
      const localeDict = await import('./dicts/no')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'pl':
    case 'pl_pl': {
      const localeDict = await import('./dicts/pl')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'pt':
    case 'pt_pt':
    case 'pt_br': {
      const localeDict = await import('./dicts/pt')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'ro':
    case 'ro_ro': {
      const localeDict = await import('./dicts/ro')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'ru':
    case 'ru_ru': {
      const localeDict = await import('./dicts/ru')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'sv':
    case 'sv_se': {
      const localeDict = await import('./dicts/sv')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'th':
    case 'th_th': {
      const localeDict = await import('./dicts/th')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'tr':
    case 'tr_tr': {
      const localeDict = await import('./dicts/tr')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'uk':
    case 'uk_ua': {
      const localeDict = await import('./dicts/uk')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'vi':
    case 'vi_vi':
    case 'vi_vn': {
      const localeDict = await import('./dicts/vi')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'zh':
    case 'zh_cn': {
      const localeDict = await import('./dicts/zh_CN')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'zh_hk': {
      const localeDict = await import('./dicts/zh_HK')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'zh_tw': {
      const localeDict = await import('./dicts/zh_TW')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }
    case 'zh_sg': {
      const localeDict = await import('./dicts/zh_CN')
      return { dict: localeDict.dict, rtl: localeDict.rtl }
    }

    default: {
      const splitLocaleID = localeID?.split('_') || []
      if (splitLocaleID.length > 1) {
        // try for locale without country code if locale with country code is not found
        return await loadDict(splitLocaleID[0])
      } else {
        throw new TypeError(`Locale ${localeID} is not supported`)
      }
    }
  }
}
